import type { QueryClient } from '@tanstack/react-query'
import type { useAddItem, useBumpUsual, useSetItemFields, useUpdateItem } from './mutations'
import { dueParse } from './dueParse'
import { repeatParse, type Repeat } from './repeatParse'
import { hereZone } from './zone'
import { hereCurrency } from './currency'
import { planGroceryAdd } from './listParse'
import { aisleOf, needsLookup } from './aisles'
import { requestAisleLookup } from './groceryAisles'
import type { Dict } from './aisleMatch'
import { fetchLinkPreview } from './linkPreview'
import { topPosition, topPositions } from './newPosition'
import type { List, ListItem } from './types'

/**
 * The write half of "add a line to a list", one function per kind, shared by
 * each kind's own screen (`TaskList`, `GroceryList`, `PurchaseList`,
 * `CollectionList`) and by Quick add. They used to be inline in the screens;
 * a second copy in the sheet would have drifted the first time a screen
 * learned something (the grocery duplicate rule has already changed twice).
 *
 * The mutations are passed in rather than made here, because the screen's
 * error banner reads *its own* mutation's state (`useWriteError(addM, …)`) —
 * a function that made its own would fail without the banner ever hearing.
 */
export interface AddDeps {
  addM: ReturnType<typeof useAddItem>
  updateM: ReturnType<typeof useUpdateItem>
  fieldsM: ReturnType<typeof useSetItemFields>
  bumpM: ReturnType<typeof useBumpUsual>
  qc: QueryClient
  /** Grocery aisles the person and the Worker have learned; read once per screen. */
  aisles: Dict
  /** The built dictionary (`useAisleDict`), or `null` before its chunk has
   *  arrived — and then no term is a miss, so the Worker is not asked. */
  dict: Dict | null
}

export interface AddOutcome {
  /** False when a write failed: the caller keeps the draft for a retry. */
  ok: boolean
  /** True when there was nothing to add (blank, or no body once parsed). */
  empty?: boolean
  /** Ids of the rows inserted. Shorter than the rows written when the server
   *  answered without one; Undo cannot reach a row it has no id for. */
  ids: string[]
  /** Open grocery rows whose count went up, with the count they had. */
  bumped: { id: string; body: string; qty: string; was: string | null }[]
  /** Open grocery rows the line named again: "milk ×3" (a bump) or "milk"
   *  (an amount that cannot be added to). */
  already: string[]
}

const result = (over: Partial<AddOutcome> = {}): AddOutcome =>
  ({ ok: true, ids: [], bumped: [], already: [], ...over })

const idOf = (res: unknown): string | null =>
  (res as { data?: { id?: string } | null } | null)?.data?.id ?? null

/** Tasks: "Pay rent tomorrow" is a task called "Pay rent" due tomorrow, and
 *  "Get wine every week by Friday" repeats (D6). Repeat is read first and
 *  then the date, and again the other way round, so either order of the
 *  two phrases comes out the same (`test/repeat-parse.test.ts`). */
export function parseTask(text: string, now = new Date()): { body: string; due_on: string | null; repeat: Repeat | null } {
  const r1 = repeatParse(text)
  const d = dueParse(r1.body, now, hereZone())
  const r2 = r1.repeat ? r1 : repeatParse(d.body)
  return { body: r2.repeat && !r1.repeat ? r2.body : d.body, due_on: d.due_on, repeat: r1.repeat ?? r2.repeat }
}

export async function addTask(d: Pick<AddDeps, 'addM'>, list: List, items: ListItem[], text: string): Promise<AddOutcome> {
  if (!text.trim()) return result({ empty: true })
  const { body, due_on, repeat } = parseTask(text)
  if (!body) return result({ empty: true })
  try {
    const res = await d.addM.mutateAsync({ listId: list.id, body, opts: {
      position: topPosition(items), due_on, repeat_every: repeat?.every ?? null, repeat_unit: repeat?.unit ?? null,
    } })
    const id = idOf(res)
    return result({ ids: id ? [id] : [] })
  } catch { return result({ ok: false }) }
}

/**
 * Groceries. One line can name several items (`2 kg potatoes, lemons ×4`),
 * and naming one already open bumps its count rather than adding a second
 * row. `planGroceryAdd` decides both; this only writes.
 */
export async function addGrocery(d: Pick<AddDeps, 'addM' | 'updateM' | 'bumpM' | 'qc' | 'aisles' | 'dict'>, list: List, items: ListItem[], text: string): Promise<AddOutcome> {
  const open = items.filter(i => !i.done_at)
  const plan = planGroceryAdd(text, open)
  if (!plan.adds.length && !plan.bumps.length && !plan.already.length) return result({ empty: true })

  const known = d.aisles
  const ids: string[] = []
  const slots = topPositions(items, plan.adds.length)
  for (const [n, { body, qty }] of plan.adds.entries()) {
    try {
      const res = await d.addM.mutateAsync({ listId: list.id, body, opts: { position: slots[n], qty } })
      const id = idOf(res)
      if (id) ids.push(id)
    } catch {
      return result({ ok: false, ids })
    }
    d.bumpM.mutate({ listId: list.id, body })
    // WP 8.7: a term neither the person's overrides, the Worker's learned
    // rows nor the dictionary can place — ask the Worker once, on add, never
    // on render, and never before the dictionary has loaded (a miss against
    // nothing is not a miss). Fire-and-forget: an unreachable Worker leaves
    // the item in Other.
    if (d.dict && aisleOf(body, known, d.dict) === 'Other' && needsLookup(body, known, d.dict)) {
      void requestAisleLookup(body, d.qc)
    }
  }
  // After the adds, so a failed insert leaves the draft for a retry that
  // does not count the same item twice.
  const bumped: AddOutcome['bumped'] = []
  for (const b of plan.bumps) {
    d.updateM.mutate({ id: b.id, patch: { qty: b.qty } })
    d.bumpM.mutate({ listId: list.id, body: b.body })
    bumped.push({ ...b, was: open.find(o => o.id === b.id)?.qty ?? null })
  }
  return result({
    ids, bumped,
    already: [...plan.bumps.map(b => `${b.body} ${b.qty}`), ...plan.already.map(a => a.body)],
  })
}

/**
 * Purchases: the text becomes the row's body; a pasted link then asks the
 * Worker's `/link-preview` for a real title, price and picture, and a 403 or
 * a page with nothing to read leaves the row exactly as pasted (rule 4's
 * shape, not a special case here). Doug answered the `robots.txt` question
 * on 27 Sept: a page pasted here is his own fetch, not a crawl.
 */
export async function addPurchase(d: Pick<AddDeps, 'addM' | 'updateM' | 'fieldsM'>, list: List, items: ListItem[], text: string): Promise<AddOutcome> {
  const trimmed = text.trim()
  if (!trimmed) return result({ empty: true })
  let id: string | null
  try {
    id = idOf(await d.addM.mutateAsync({ listId: list.id, body: trimmed, opts: { position: topPosition(items) } }))
  } catch {
    return result({ ok: false }) // useWriteError reads addM's own state for the banner
  }
  if (id && /^https?:\/\//i.test(trimmed)) void fillFromPreview(d, id, trimmed)
  return result({ ids: id ? [id] : [] })
}

async function fillFromPreview(d: Pick<AddDeps, 'updateM' | 'fieldsM'>, id: string, pastedUrl: string) {
  let preview: Awaited<ReturnType<typeof fetchLinkPreview>>
  try {
    preview = await fetchLinkPreview(pastedUrl)
  } catch {
    return
  }
  if (preview.title == null && preview.price_cents == null && preview.image_url == null) return
  d.updateM.mutate({ id, patch: { body: preview.title ?? pastedUrl, link: pastedUrl } })
  if (preview.price_cents != null || preview.image_url != null) {
    d.fieldsM.mutate({ id, patch: { price_cents: preview.price_cents, currency: preview.currency ?? hereCurrency(), image_url: preview.image_url } })
  }
}

/** Collections, and a checklist item with no section (it files under
 *  "Other"): the text as typed. */
export async function addPlain(d: Pick<AddDeps, 'addM'>, list: List, items: ListItem[], text: string): Promise<AddOutcome> {
  const body = text.trim()
  if (!body) return result({ empty: true })
  try {
    const res = await d.addM.mutateAsync({ listId: list.id, body, opts: { position: topPosition(items) } })
    const id = idOf(res)
    return result({ ids: id ? [id] : [] })
  } catch { return result({ ok: false }) }
}

/** The path the list's own screen takes, chosen by the list's kind. */
export function addToList(d: AddDeps, list: List, items: ListItem[], text: string): Promise<AddOutcome> {
  switch (list.kind) {
    case 'task': return addTask(d, list, items, text)
    case 'grocery': return addGrocery(d, list, items, text)
    case 'buy': return addPurchase(d, list, items, text)
    default: return addPlain(d, list, items, text)
  }
}
